import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { verifyBooking } from '../services/BookingService';
import QrScanner from 'qr-scanner';

interface BookingInfo {
  bookingCode: string;
  status: string;
  customerName: string;
  programTitle: string;
  date: string;
  venue: string;
  totalAmount: number;
  tickets: Array<{
    ticketName: string;
    quantity: number;
  }>;
}

const BookingVerificationPage: React.FC = () => {
  const [bookingCode, setBookingCode] = useState('');
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);

  const handleVerify = async (code?: string) => {
    const codeToVerify = code || bookingCode.trim();
    if (!codeToVerify) return;

    setLoading(true);
    setError(null);
    setBookingInfo(null);

    try {
      const data = await verifyBooking(codeToVerify);
      setBookingInfo(data);
    } catch (err) {
      setError('Booking not found or invalid booking code');
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    if (!videoRef.current) return;
    
    try {
      setShowScanner(true);
      setError(null);

      const hasCamera = await QrScanner.hasCamera();
      
      if (!hasCamera) {
        setError('No camera found on this device');
        setShowScanner(false);
        return;
      }

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          setBookingCode(result.data.toUpperCase());
          stopScanner();
          handleVerify(result.data.toUpperCase());
        },
        {
          highlightScanRegion: true,
          highlightCodeOutline: true
        }
      );
      
      await qrScannerRef.current.start();
    } catch (err: any) {
      let errorMsg = 'Camera access failed';
      if (err.name === 'NotAllowedError') {
        errorMsg = 'Camera permission denied. Please allow camera access.';
      } else if (err.name === 'NotFoundError') {
        errorMsg = 'No camera found on this device.';
      }
      
      setError(errorMsg);
      setShowScanner(false);
    }
  };

  const stopScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setShowScanner(false);
  };

  useEffect(() => {
    return () => stopScanner();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge bg="success">Confirmed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '20px' }}>
              <Card.Header 
                className="text-white text-center border-0" 
                style={{ 
                  background: 'linear-gradient(45deg, #810081, #a855f7)',
                  borderRadius: '20px 20px 0 0',
                  padding: '2rem'
                }}
              >
                <h3 className="mb-2">
                  <i className="bi bi-qr-code-scan me-3"></i>
                  Ticket Verification
                </h3>
                <p className="mb-0 opacity-90">Scan QR code or enter booking code</p>
              </Card.Header>

              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  {!showScanner ? (
                    <>
                      <Button 
                        variant="primary"
                        size="lg"
                        onClick={() => {
                          setShowScanner(true);
                          setTimeout(startScanner, 100);
                        }}
                        className="mb-3 px-5 py-3"
                        style={{ 
                          borderRadius: '50px',
                          background: 'linear-gradient(45deg, #667eea, #764ba2)',
                          border: 'none',
                          fontSize: '1.1rem'
                        }}
                      >
                        <i className="bi bi-camera me-2"></i>
                        Scan QR Code
                      </Button>
                      
                      <div className="text-muted mb-3">
                        <small>or</small>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="position-relative d-inline-block">
                        <video 
                          ref={videoRef}
                          className="rounded-3"
                          style={{ 
                            width: '100%',
                            maxWidth: '400px',
                            height: 'auto'
                          }}
                          playsInline
                          muted
                        />
                        
                        <div 
                          className="position-absolute"
                          style={{
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '200px',
                            height: '200px',
                            border: '3px solid #fff',
                            borderRadius: '20px',
                            pointerEvents: 'none',
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                          }}
                        />
                      </div>
                      
                      <div className="mt-3">
                        <Button 
                          variant="outline-danger"
                          onClick={stopScanner}
                          className="px-4"
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          Stop Scanner
                        </Button>
                      </div>
                      
                      <p className="text-muted mt-3 mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        Hold QR code steady within the white frame
                      </p>
                    </>
                  )}
                </div>

                <Form onSubmit={(e) => { e.preventDefault(); handleVerify(); }}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Enter Booking Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="BK1234567890ABCD"
                      value={bookingCode}
                      onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
                      className="text-center py-3"
                      style={{ 
                        letterSpacing: '2px',
                        fontSize: '1.1rem',
                        borderRadius: '15px',
                        border: '2px solid #e9ecef'
                      }}
                    />
                  </Form.Group>
                  
                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={loading || !bookingCode.trim()}
                    className="w-100 py-3"
                    style={{ 
                      background: 'linear-gradient(45deg, #FFD600, #ff9800)',
                      border: 'none',
                      borderRadius: '15px',
                      color: '#333',
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-2"></i>
                        Verify Booking
                      </>
                    )}
                  </Button>
                </Form>

                {error && (
                  <Alert variant="danger" className="mt-4 border-0" style={{ borderRadius: '15px' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {bookingInfo && (
                  <Card className="mt-4 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                    <Card.Header 
                      className="bg-success text-white d-flex justify-content-between align-items-center"
                      style={{ borderRadius: '15px 15px 0 0' }}
                    >
                      <h5 className="mb-0">
                        <i className="bi bi-check-circle me-2"></i>
                        Booking Verified
                      </h5>
                      {getStatusBadge(bookingInfo.status)}
                    </Card.Header>
                    <Card.Body className="p-4">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                            <span className="fw-bold">Booking Code:</span>
                            <span className="font-monospace text-primary">{bookingInfo.bookingCode}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                            <span className="fw-bold">Customer:</span>
                            <span>{bookingInfo.customerName}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                            <span className="fw-bold">Program:</span>
                            <span>{bookingInfo.programTitle}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                            <span className="fw-bold">Date:</span>
                            <span>{new Date(bookingInfo.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                            <span className="fw-bold">Venue:</span>
                            <span>{bookingInfo.venue}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="p-3 bg-light rounded">
                            <div className="fw-bold mb-2">Tickets:</div>
                            {bookingInfo.tickets?.map((ticket, index) => (
                              <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                                <span>{ticket.ticketName}</span>
                                <Badge bg="primary">{ticket.quantity}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-3 bg-success text-white rounded">
                            <span className="fw-bold">Amount:</span>
                            <span className="fs-4">₹{bookingInfo.totalAmount}</span>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BookingVerificationPage;